import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiClient } from '@/lib/apiClient';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ONBOARDING_STATUS_KEY } from '../hooks/useOnboarding';
import { StepFooter } from './StepFooter';

interface FaqDraft {
  question: string;
  answer: string;
}

const PLACEHOLDERS: FaqDraft[] = [
  { question: 'Do you deliver outside Dhaka?', answer: 'Yes, nationwide in 3–5 days. Delivery is ৳120.' },
  { question: 'Do you accept bKash?', answer: 'Yes: bKash, Nagad and cash on delivery.' },
  { question: 'Can I return an item?', answer: 'Within 7 days if unused, with the receipt.' },
];

interface KnowledgeStepProps {
  hasBusiness: boolean;
  onDone: () => void;
}

export function KnowledgeStep({ hasBusiness, onDone }: KnowledgeStepProps) {
  const queryClient = useQueryClient();
  const [faqs, setFaqs] = useState<FaqDraft[]>(PLACEHOLDERS.map(() => ({ question: '', answer: '' })));
  const [about, setAbout] = useState('');

  // Business knowledge is one text blob that PUT replaces whole, so load what is already there:
  // re-running the wizard must not overwrite it with an empty box.
  const saved = useQuery({
    queryKey: ['knowledge', 'business-docs'],
    queryFn: () =>
      api.get<{ businessKnowledge: string }>('/api/chatbot/knowledge-docs').then((d) => d?.businessKnowledge ?? ''),
    enabled: hasBusiness,
  });
  const [prefilled, setPrefilled] = useState(false);
  useEffect(() => {
    if (prefilled || !saved.isSuccess) return;
    setAbout(saved.data ?? '');
    setPrefilled(true);
  }, [saved.isSuccess, saved.data, prefilled]);

  const filled = faqs.filter((f) => f.question.trim() && f.answer.trim());
  const aboutChanged = hasBusiness && about.trim() !== (saved.data ?? '').trim();

  const save = useMutation({
    mutationFn: async () => {
      // Clear each row the moment it is saved, so a retry after a failure part-way through posts
      // only the rows that did not make it, never a duplicate.
      for (let i = 0; i < faqs.length; i++) {
        const f = faqs[i];
        if (!f.question.trim() || !f.answer.trim()) continue;
        await apiClient.post('/api/faqs', { question: f.question.trim(), answer: f.answer.trim() });
        setFaqs((list) => list.map((row, j) => (j === i ? { question: '', answer: '' } : row)));
      }
      // Rebuilds the AI's knowledge index, so only when the text actually changed.
      if (aboutChanged) {
        await apiClient.put('/api/chatbot/knowledge-docs', { businessKnowledge: about.trim() });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ONBOARDING_STATUS_KEY });
      queryClient.invalidateQueries({ queryKey: ['knowledge'] });
      onDone();
    },
  });

  const update = (i: number, patch: Partial<FaqDraft>) =>
    setFaqs((list) => list.map((f, j) => (j === i ? { ...f, ...patch } : f)));

  const nothingToSave = filled.length === 0 && !aboutChanged;

  return (
    <div>
      <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Teach the AI the basics</h2>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
        Add the questions customers ask most. The AI answers from these, and you can add products and
        more answers later on the Knowledge page.
      </p>

      <div className="mt-6 space-y-5">
        {faqs.map((f, i) => (
          <fieldset key={i} className="space-y-2 rounded-lg border border-slate-200 dark:border-slate-800 p-4">
            <legend className="px-1 text-xs font-medium text-slate-500 dark:text-slate-400">Question {i + 1}</legend>
            <Label htmlFor={`onb-faq-q-${i}`} className="sr-only">
              Question {i + 1}
            </Label>
            <Input
              id={`onb-faq-q-${i}`}
              value={f.question}
              onChange={(e) => update(i, { question: e.target.value })}
              placeholder={PLACEHOLDERS[i].question}
              maxLength={500}
            />
            <Label htmlFor={`onb-faq-a-${i}`} className="sr-only">
              Answer {i + 1}
            </Label>
            <Textarea
              id={`onb-faq-a-${i}`}
              value={f.answer}
              onChange={(e) => update(i, { answer: e.target.value })}
              placeholder={PLACEHOLDERS[i].answer}
              maxLength={4000}
              rows={2}
            />
          </fieldset>
        ))}

        {hasBusiness ? (
          <div className="space-y-1.5">
            <Label htmlFor="onb-about">Anything else the AI should know? (optional)</Label>
            <Textarea
              id="onb-about"
              value={about}
              onChange={(e) => setAbout(e.target.value)}
              placeholder="Opening hours, delivery areas, payment methods, return policy…"
              rows={4}
            />
          </div>
        ) : (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Add your business details (step 1) to also give the AI a free-text description of your policies.
          </p>
        )}
      </div>

      <StepFooter
        onSkip={onDone}
        primaryLabel={nothingToSave ? 'Continue' : 'Save and continue'}
        onPrimary={nothingToSave ? onDone : () => save.mutate()}
        busy={save.isPending}
      />
    </div>
  );
}
